import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, RotateCcw } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  HomeworkOverviewPeriod,
  homeworkOverviewPeriods,
} from "../api/contracts";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  HomeworkOverviewMetrics,
  ReportWindowBanner,
} from "../components/ReportVisuals";
import { usePortalData } from "../hooks/usePortalData";
import { isActivatedClientStatus } from "../utils/clientStatus";
import { formatPortalDate } from "../utils/dateTime";

const periodLabels: Record<HomeworkOverviewPeriod, string> = {
  "this-week": "This Week",
  "last-30-days": "Last 30 Days",
  custom: "Custom",
};

function initialFilters() {
  const params = new URLSearchParams(window.location.search);
  const requestedPeriod = params.get("period") as HomeworkOverviewPeriod | null;
  return {
    patientId: params.get("patientId")?.trim() ?? "",
    period: homeworkOverviewPeriods.includes(
      requestedPeriod as HomeworkOverviewPeriod,
    )
      ? (requestedPeriod as HomeworkOverviewPeriod)
      : ("last-30-days" as const),
    startDate: params.get("startDate")?.trim() ?? "",
    endDate: params.get("endDate")?.trim() ?? "",
  };
}

export function HomeworkOverviewPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const initial = useMemo(initialFilters, []);
  const [patientId, setPatientId] = useState(initial.patientId);
  const [period, setPeriod] = useState<HomeworkOverviewPeriod>(initial.period);
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const customDatesValid =
    period !== "custom" ||
    (Boolean(startDate) && Boolean(endDate) && startDate <= endDate);

  useEffect(() => {
    const url = new URL(window.location.href);
    patientId
      ? url.searchParams.set("patientId", patientId)
      : url.searchParams.delete("patientId");
    period === "last-30-days"
      ? url.searchParams.delete("period")
      : url.searchParams.set("period", period);
    if (period === "custom") {
      startDate
        ? url.searchParams.set("startDate", startDate)
        : url.searchParams.delete("startDate");
      endDate
        ? url.searchParams.set("endDate", endDate)
        : url.searchParams.delete("endDate");
    } else {
      url.searchParams.delete("startDate");
      url.searchParams.delete("endDate");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  }, [endDate, patientId, period, startDate]);

  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getHomeworkOverview(
        clinicianCode,
        patientId,
        period,
        startDate,
        endDate,
        signal,
      ),
    [clinicianCode, endDate, patientId, period, startDate],
  );
  const state = usePortalData(request, { enabled: customDatesValid });
  const responseData =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data =
    responseData?.filters.period === period &&
    responseData.filters.patientId === (patientId || null) &&
    (period !== "custom" ||
      (responseData.filters.startDate === startDate &&
        responseData.filters.endDate === endDate))
      ? responseData
      : undefined;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const clients = useMemo(
    () =>
      (responseData?.clientOptions ?? []).filter((client) =>
        isActivatedClientStatus(client.status),
      ),
    [responseData?.clientOptions],
  );

  useEffect(() => {
    if (
      responseData &&
      patientId &&
      !clients.some((client) => client.patientId === patientId)
    ) {
      setPatientId("");
    }
  }, [clients, patientId, responseData]);

  const resetFilters = () => {
    setPatientId("");
    setPeriod("last-30-days");
    setStartDate("");
    setEndDate("");
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="homework-overview"
      clinicianCode={clinicianCode}
      therapistRole={responseData?.therapist?.role}
    >
      <PortalHeader
        title="Homework Overview"
        clinicianName={responseData?.therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Reports" }, { label: "Homework Overview" }]}
          />
        }
      />

      <div className="cp-page-content cp-page-content--wide cp-page-content--profile">
        <section className="cp-homework-overview">
          <div className="cp-homework-overview__intro">
            <div>
              <h2>Homework Overview</h2>
              <p>
                Review assignments and clinical activity for homework assigned
                during the selected period.
              </p>
            </div>
            <button
              type="button"
              className="cp-button cp-button--secondary"
              disabled={
                state.status === "loading" || state.status === "refreshing"
              }
              onClick={state.refresh}
            >
              <RefreshCw aria-hidden="true" />
              {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="cp-homework-overview__filters">
            <label>
              <span>Client</span>
              <select
                value={patientId}
                onChange={(event) => setPatientId(event.target.value)}
              >
                <option value="">All My Clients</option>
                {clients.map((client) => (
                  <option key={client.patientId} value={client.patientId}>
                    {client.nickname} · {client.accessCode}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Assigned Period</span>
              <select
                value={period}
                onChange={(event) =>
                  setPeriod(event.target.value as HomeworkOverviewPeriod)
                }
              >
                {homeworkOverviewPeriods.map((option) => (
                  <option key={option} value={option}>
                    {periodLabels[option]}
                  </option>
                ))}
              </select>
            </label>
            {period === "custom" ? (
              <>
                <label>
                  <span>Start Date</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </label>
                <label>
                  <span>End Date</span>
                  <input
                    type="date"
                    min={startDate || undefined}
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </label>
              </>
            ) : null}
            <button
              type="button"
              className="cp-button cp-button--secondary cp-homework-overview__reset"
              onClick={resetFilters}
            >
              <RotateCcw aria-hidden="true" />
              Reset Filters
            </button>
          </div>

          {period === "custom" && !customDatesValid ? (
            <EmptyState
              title="Choose a valid date range"
              description="Select a start date and an end date on or after the start date."
            />
          ) : null}
          {customDatesValid &&
          (state.status === "loading" ||
            (state.status === "refreshing" && !data)) ? (
            <LoadingState label="Loading homework overview" />
          ) : null}
          {customDatesValid && state.status === "error" ? (
            <ErrorState error={state.error} onRetry={state.refresh} />
          ) : null}
          {data && !available ? (
            <EmptyState
              title="Report unavailable"
              description="This client is unavailable or is not assigned to your account."
            />
          ) : null}
          {available && data ? (
            <div className="cp-homework-overview__body">
              <ReportWindowBanner className="cp-homework-overview__window">
                Assigned-date window: {formatPortalDate(data.filters.startDate)}
                {" through "}
                {formatPortalDate(data.filters.endDate)} · As of{" "}
                {formatPortalDate(data.asOfDate)} ({data.timezone})
              </ReportWindowBanner>
              {data.summary.assigned === 0 ? (
                <p className="cp-homework-overview__empty-cohort" role="status">
                  No homework was assigned during this period. Rates and
                  averages are shown as em dashes.
                </p>
              ) : null}
              <HomeworkOverviewMetrics
                summary={data.summary}
                clinicalMetrics={data.clinicalMetrics}
              />
            </div>
          ) : null}
        </section>
      </div>
    </ClinicianPortalLayout>
  );
}
